import axios from 'axios';
import config from '../config';

export interface VisitData {
  id: string;
  user_id: string;
  venue_id: string;
  location_id?: string;
  check_in_time: string;
  check_out_time?: string;
  status: 'active' | 'expired';
  visit_type: 'GENERAL' | 'LOCATION_SPECIFIC' | 'ONLINE';
  expires_at: string;
}

export interface CreateVisitRequest {
  venue_id: string;
  location_id?: string;
}

export interface VisitAnalytics {
  total_visits: number;
  physical_visits: number;
  online_visits: number;
  active_visits: number;
  active_physical_visits: number;
  active_online_visits: number;
  time_range: '24h' | 'today' | 'lifetime';
  time_range_label: string;
  venue_id?: string;
}

export const createVisit = async (visitData: CreateVisitRequest): Promise<VisitData> => {
  try {
    const response = await axios.post(`${config.API_BASE_URL}/api/visits`, visitData);
    return response.data;
  } catch (error) {
    console.error('Error creating visit:', error);
    throw error;
  }
};

export const createOnlineVisit = async (visitData: CreateVisitRequest): Promise<VisitData> => {
  try {
    const response = await axios.post(`${config.API_BASE_URL}/api/visits/online`, visitData);
    return response.data;
  } catch (error) {
    console.error('Error creating online visit:', error);
    throw error;
  }
};

export const getActiveVisit = async (): Promise<VisitData | null> => {
  try {
    const response = await axios.get(`${config.API_BASE_URL}/api/visits/active`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null; // No active visit
    }
    console.error('Error getting active visit:', error);
    throw error;
  }
};

export const getUserVisits = async (page: number = 1, pageSize: number = 20, status?: string) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    
    if (status) {
      params.append('status', status);
    }
    
    const response = await axios.get(`${config.API_BASE_URL}/api/visits?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error getting user visits:', error);
    throw error;
  }
};

export const addLocationToVisit = async (visitId: string, locationId: string) => {
  try {
    const response = await axios.post(
      `${config.API_BASE_URL}/api/visits/${visitId}/locations`,
      null,
      { params: { location_id: locationId } }
    );
    return response.data;
  } catch (error) {
    console.error('Error adding location to visit:', error);
    throw error;
  }
};

export const getVisitAnalytics = async (
  venueId?: string, 
  days: number = 7, 
  timeRange: '24h' | 'today' | 'lifetime' = 'lifetime'
): Promise<VisitAnalytics> => {
  try {
    const params = new URLSearchParams({
      days: days.toString(),
      time_range: timeRange,
    });
    
    if (venueId) {
      params.append('venue_id', venueId);
    }
    
    const response = await axios.get(`${config.API_BASE_URL}/api/visits/analytics?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error getting visit analytics:', error);
    throw error;
  }
};

export const getVenueVisits = async (venueId: string, page: number = 1, limit: number = 100, uniqueVisitsOnly: boolean = false): Promise<{items: VisitData[], total: number, page: number, limit: number}> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (uniqueVisitsOnly) {
      params.append('unique_visits_only', 'true');
    }
    
    const response = await axios.get(`${config.API_BASE_URL}/api/visits/venue/${venueId}?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching venue visits:', error);
    throw error;
  }
};

export const getChartAnalytics = async (venueId: string, chartType: 'dietary' | 'health' | 'visit_types'): Promise<{name: string, count: number}[]> => {
  try {
    const response = await axios.get(`${config.API_BASE_URL}/api/visits/venue/${venueId}?page=1&limit=1000&unique_visits_only=true`);
    const visits = response.data.items || [];
    
    
    // Define all possible values for each chart type
    const allPossibleValues = {
      dietary: [
        'vegan', 'vegetarian', 'pescatarian', 'gluten_free', 'dairy_free', 
        'nut_free', 'egg_free', 'soy_free', 'shellfish_free', 'fish_free', 
        'sesame_free', 'low_fodmap', 'keto', 'paleo', 'atkins', 
        'corn_free', 'mustard_free', 'low_carb', 'none'
      ],
      health: [
        'high_blood_pressure', 'diabetes', 'heart_disease', 'high_cholesterol',
        'kidney_disease', 'celiac_disease', 'lactose_intolerance', 'ibs',
        'osteoporosis', 'anemia', 'iron_deficiency', 'high_protein_needs',
        'weight_loss', 'pregnancy', 'sibo', 'pcos', 'high_carbs', 'none'
      ],
      visit_types: [
        'physical', 'online'
      ],
    };
    
    const counts: {[key: string]: number} = {};
    
    // Initialize all possible values with 0
    allPossibleValues[chartType].forEach(value => {
      counts[value] = 0;
    });
    
    // Count actual occurrences
    visits.forEach((visit: any) => {
      if (chartType === 'dietary') {
        if (visit.user?.dietary_preferences && visit.user.dietary_preferences.length > 0) {
          visit.user.dietary_preferences.forEach((pref: string) => {
            if (counts.hasOwnProperty(pref)) {
              counts[pref] = (counts[pref] || 0) + 1;
            }
          });
        } else {
          counts['none'] = (counts['none'] || 0) + 1;
        }
      } else if (chartType === 'health') {
        if (visit.user?.health_concerns && visit.user.health_concerns.length > 0) {
          visit.user.health_concerns.forEach((concern: string) => {
            if (counts.hasOwnProperty(concern)) {
              counts[concern] = (counts[concern] || 0) + 1;
            }
          });
        } else {
          counts['none'] = (counts['none'] || 0) + 1;
        }
      } else if (chartType === 'visit_types') {
        // Count visit types based on visit_type field
        if (visit.visit_type === 'ONLINE') {
          counts['online'] = (counts['online'] || 0) + 1;
        } else {
          // All other visit types (general, location_specific) are considered physical
          counts['physical'] = (counts['physical'] || 0) + 1;
        }
      }
    });
    
    // Convert to array format, sorted by count (highest first)
    return Object.entries(counts).map(([name, count]) => ({
      name,
      count: count as number
    })).sort((a, b) => b.count - a.count);
  } catch (error) {
    console.error(`Error fetching ${chartType} analytics:`, error);
    throw error;
  }
};


// Keep the old function for backward compatibility
export const getDietPreferenceAnalytics = async (venueId: string): Promise<{preference: string, count: number}[]> => {
  const data = await getChartAnalytics(venueId, 'dietary');
  return data.map(item => ({ preference: item.name, count: item.count }));
};
